import React from 'react';
import { motion } from 'framer-motion';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { Table } from 'antd';
import { TrendingUp, BarChart3, PieChart as PieChartIcon, Table as TableIcon } from 'lucide-react';

const COLORS = [
  '#8b5cf6', // Purple
  '#3b82f6', // Blue
  '#10b981', // Green
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#84cc16', // Lime
];

const CustomTooltip = ({ active, payload, label, isDark }) => {
  if (active && payload && payload.length) {
    return (
      <div className={`rounded-lg p-3 shadow-lg border backdrop-blur-md ${
        isDark 
          ? 'bg-gray-800/95 border-gray-700 text-gray-100' 
          : 'bg-white border-gray-200 text-gray-900'
      }`}>
        <p className={`font-semibold mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          {label}
        </p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const VisualizationRenderer = ({ data, isDark }) => {
  // Debug logging to identify why charts aren't rendering
  React.useEffect(() => {
    if (data) {
      console.log('📊 [VisualizationRenderer] Received data:', {
        hasData: !!data,
        chartType: data.chartType,
        type: data.type,
        title: data.title,
        hasDataArray: !!(data.data && Array.isArray(data.data)),
        dataLength: data.data?.length || 0,
        hasConfig: !!data.config,
        fullData: data
      });
      
      if (!data.chartType && !data.type) {
        console.error('❌ [VisualizationRenderer] MISSING chartType - component will return null!');
      }
      
      if (!data.data || !Array.isArray(data.data) || data.data.length === 0) {
        console.warn('⚠️ [VisualizationRenderer] Missing or empty data array');
      }
    } else {
      console.warn('⚠️ [VisualizationRenderer] No data prop provided');
    }
  }, [data]);
  
  // Support both chartType and type fields (for backward compatibility)
  const chartType = data?.chartType || data?.type;
  
  if (!data || !chartType) {
    console.warn('❌ [VisualizationRenderer] Returning null - missing data or chartType', {
      hasData: !!data,
      chartType: data?.chartType,
      type: data?.type,
      resolvedChartType: chartType
    });
    return null;
  }

  const getIcon = () => {
    switch (chartType) {
      case 'line':
        return <TrendingUp className="h-4 w-4" />;
      case 'bar':
        return <BarChart3 className="h-4 w-4" />;
      case 'pie':
        return <PieChartIcon className="h-4 w-4" />;
      case 'table':
        return <TableIcon className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const renderChart = () => {
    if (!data.data || !Array.isArray(data.data) || data.data.length === 0) {
      return (
        <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          No data available for visualization
        </div>
      );
    }

    const config = data.config || {};
    
    // For pie charts, nameKey and valueKey can be at root level or in config
    // If not provided, auto-detect from first data entry
    let nameKey = data.nameKey || config.nameKey;
    let valueKey = data.valueKey || config.valueKey;
    
    // Auto-detect keys if not provided (for pie charts especially)
    if ((chartType === 'pie' || !nameKey || !valueKey) && data.data && data.data.length > 0) {
      const firstEntry = data.data[0];
      const keys = Object.keys(firstEntry);
      
      // Auto-detect: find a key that looks like a name/label (string, not number)
      if (!nameKey && keys.length > 0) {
        // Look for common name-like keys
        const nameLikeKeys = keys.filter(key => 
          ['name', 'label', 'project', 'category', 'item', 'title'].includes(key.toLowerCase()) ||
          (typeof firstEntry[key] === 'string' && isNaN(firstEntry[key]))
        );
        nameKey = nameLikeKeys[0] || keys[0];
      }
      
      // Auto-detect: find a key that looks like a value (number)
      if (!valueKey && keys.length > 0) {
        // Look for common value-like keys
        const valueLikeKeys = keys.filter(key => 
          ['value', 'hours', 'count', 'amount', 'total', 'sum'].includes(key.toLowerCase()) ||
          (typeof firstEntry[key] === 'number')
        );
        valueKey = valueLikeKeys[0] || (keys.length > 1 ? keys[1] : keys[0]);
      }
    }
    
    // Fallback defaults
    nameKey = nameKey || 'name';
    valueKey = valueKey || 'value';
    
    // Log detected keys for pie charts
    if (chartType === 'pie') {
      console.log('📊 [VisualizationRenderer] Pie chart keys:', {
        nameKey,
        valueKey,
        wasAutoDetected: !(data.nameKey || config.nameKey),
        firstEntryKeys: data.data && data.data.length > 0 ? Object.keys(data.data[0]) : []
      });
    }
    
    // Initialize xKey and yKey with defaults
    let xKey = config.xKey || data.xKey || 'date';
    let yKey = config.yKey || data.yKey || 'value';
    
    // Auto-detect xKey and yKey for bar/line charts if not provided
    if ((chartType === 'bar' || chartType === 'line') && data.data && data.data.length > 0) {
      const firstEntry = data.data[0];
      const keys = Object.keys(firstEntry);
      
      // Auto-detect xKey (date/label field) - only if not already set
      if (!config.xKey && !data.xKey) {
        const dateLikeKeys = keys.filter(key => 
          ['date', 'Date', 'timestamp', 'time', 'day', 'x', 'label', 'name'].includes(key) ||
          (typeof firstEntry[key] === 'string' && /^\d{4}-\d{2}-\d{2}/.test(firstEntry[key]))
        );
        if (dateLikeKeys.length > 0) {
          xKey = dateLikeKeys[0];
        }
      }
      
      // Auto-detect yKey (value field) - only if not already set
      if (!config.yKey && !data.yKey) {
        const valueLikeKeys = keys.filter(key => 
          ['value', 'count', 'number', 'members', 'active', 'inactive', 'y', 'amount', 'total', 'sum'].includes(key.toLowerCase()) ||
          (typeof firstEntry[key] === 'number')
        );
        if (valueLikeKeys.length > 0) {
          yKey = valueLikeKeys[0];
        }
      }
      
      // Debug logging
      console.log('📊 [VisualizationRenderer] Bar/Line chart keys detected:', {
        chartType,
        xKey,
        yKey,
        wasAutoDetected: !(config.xKey || data.xKey || config.yKey || data.yKey),
        availableKeys: keys,
        firstEntry
      });
    }
    
    const xAxisLabel = config.xAxis || xKey;
    const yAxisLabel = config.yAxis || yKey;

    // Helper function to detect if a value looks like a date
    const isDateValue = (value) => {
      if (!value) return false;
      // Check if it's a date string (ISO format, timestamp, or common date formats)
      if (typeof value === 'string') {
        // ISO date format: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
        if (/^\d{4}-\d{2}-\d{2}/.test(value)) return true;
        // Timestamp (number as string)
        if (/^\d{10,13}$/.test(value)) return true;
      }
      // Check if it's a Date object or timestamp number
      if (value instanceof Date) return true;
      if (typeof value === 'number' && value > 1000000000) return true;
      return false;
    };

    // Helper function to format date for display
    const formatDateTick = (value) => {
      if (!value) return '';
      
      try {
        let date;
        if (typeof value === 'string') {
          // Try parsing ISO date or timestamp string
          if (/^\d{10,13}$/.test(value)) {
            date = new Date(parseInt(value));
          } else {
            date = new Date(value);
          }
        } else if (typeof value === 'number') {
          date = new Date(value);
        } else if (value instanceof Date) {
          date = value;
        } else {
          return String(value);
        }

        // Check if date is valid
        if (isNaN(date.getTime())) {
          return String(value);
        }

        // Format based on data range
        const dataLength = data.data?.length || 0;
        if (dataLength <= 7) {
          // Short range: show full date "Jan 15"
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } else if (dataLength <= 30) {
          // Medium range: show "MM/DD"
          return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
        } else {
          // Long range: show "MM/DD" or just month
          return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
        }
      } catch (error) {
        return String(value);
      }
    };

    // Check if xKey contains date values
    const sampleValue = data.data && data.data.length > 0 ? data.data[0][xKey] : null;
    const isDateField = isDateValue(sampleValue) || 
                        xKey.toLowerCase().includes('date') || 
                        xKey.toLowerCase().includes('time');

    // Calculate interval for date labels (show every Nth label to avoid crowding)
    const dataLength = data.data?.length || 0;
    const calculateInterval = () => {
      if (dataLength <= 7) return 0; // Show all labels
      if (dataLength <= 14) return 1; // Show every other
      if (dataLength <= 30) return Math.floor(dataLength / 7); // Show ~7 labels
      return Math.floor(dataLength / 10); // Show ~10 labels for long ranges
    };

    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.data} margin={{ top: 5, right: 20, left: 0, bottom: isDateField ? 40 : 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
              <XAxis 
                dataKey={xKey} 
                stroke={isDark ? '#9ca3af' : '#6b7280'}
                tick={{ 
                  fill: isDark ? '#9ca3af' : '#6b7280', 
                  fontSize: 12,
                  angle: isDateField && dataLength > 7 ? -45 : 0,
                  textAnchor: isDateField && dataLength > 7 ? 'end' : 'middle'
                }}
                tickFormatter={isDateField ? formatDateTick : undefined}
                interval={isDateField ? calculateInterval() : 0}
                label={{ 
                  value: xAxisLabel, 
                  position: 'insideBottom', 
                  offset: isDateField ? -10 : -5, 
                  fill: isDark ? '#9ca3af' : '#6b7280' 
                }}
              />
              <YAxis 
                stroke={isDark ? '#9ca3af' : '#6b7280'}
                tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', fill: isDark ? '#9ca3af' : '#6b7280' }}
              />
              <Tooltip content={<CustomTooltip isDark={isDark} />} />
              <Legend 
                wrapperStyle={{ color: isDark ? '#d1d5db' : '#374151' }}
              />
              <Line 
                type="monotone" 
                dataKey={yKey} 
                stroke={config.color || COLORS[0]} 
                strokeWidth={2}
                dot={{ fill: config.color || COLORS[0], r: 4 }}
                activeDot={{ r: 6 }}
                name={yAxisLabel}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.data} margin={{ top: 5, right: 20, left: 0, bottom: isDateField ? 40 : 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
              <XAxis 
                dataKey={xKey} 
                stroke={isDark ? '#9ca3af' : '#6b7280'}
                tick={{ 
                  fill: isDark ? '#9ca3af' : '#6b7280', 
                  fontSize: 12,
                  angle: isDateField && dataLength > 7 ? -45 : 0,
                  textAnchor: isDateField && dataLength > 7 ? 'end' : 'middle'
                }}
                tickFormatter={isDateField ? formatDateTick : undefined}
                interval={isDateField ? calculateInterval() : 0}
                label={{ 
                  value: xAxisLabel, 
                  position: 'insideBottom', 
                  offset: isDateField ? -10 : -5, 
                  fill: isDark ? '#9ca3af' : '#6b7280' 
                }}
              />
              <YAxis 
                stroke={isDark ? '#9ca3af' : '#6b7280'}
                tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', fill: isDark ? '#9ca3af' : '#6b7280' }}
              />
              <Tooltip content={<CustomTooltip isDark={isDark} />} />
              <Legend 
                wrapperStyle={{ color: isDark ? '#d1d5db' : '#374151' }}
              />
              <Bar 
                dataKey={yKey} 
                fill={config.color || COLORS[0]}
                radius={[8, 8, 0, 0]}
                name={yAxisLabel}
              />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        // For pie chart, use nameKey for labels and valueKey for values
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => {
                  const name = entry[nameKey] || entry.name || 'Unknown';
                  const value = entry[valueKey] || entry.value || 0;
                  return `${name}: ${value}`;
                }}
                outerRadius={100}
                fill="#8884d8"
                dataKey={valueKey}
                nameKey={nameKey}
              >
                {data.data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const entry = payload[0].payload;
                    return (
                      <div className={`rounded-lg p-3 shadow-lg border backdrop-blur-md ${
                        isDark 
                          ? 'bg-gray-800/95 border-gray-700 text-gray-100' 
                          : 'bg-white border-gray-200 text-gray-900'
                      }`}>
                        <p className={`font-semibold mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {entry[nameKey] || entry.name || 'Unknown'}
                        </p>
                        <p className="text-sm" style={{ color: payload[0].color }}>
                          {`${valueKey}: ${entry[valueKey] || entry.value || 0}`}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'table':
        if (!data.columns || !Array.isArray(data.columns)) {
          return (
            <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Table columns not defined
            </div>
          );
        }

        const columns = data.columns.map(col => ({
          title: col.charAt(0).toUpperCase() + col.slice(1).replace(/([A-Z])/g, ' $1'),
          dataIndex: col,
          key: col,
          render: (text) => (
            <span>
              {text || 'N/A'}
            </span>
          ),
        }));

        return (
          <div className={isDark ? 'dark-table' : ''}>
            <Table
              dataSource={data.data}
              columns={columns}
              pagination={{ pageSize: 10 }}
              className={isDark ? 'dark-table' : ''}
              rowKey={(record, index) => record.key || record.id || index}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className={`rounded-xl p-4 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}
    >
      {data.title && (
        <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          {getIcon()}
          <span>{data.title}</span>
        </h3>
      )}
      {renderChart()}
    </motion.div>
  );
};

export default VisualizationRenderer;
